import React, { useState } from 'react';
import { ArrowRight, FileText, TrendingUp, Calculator, Shield, ChevronDown, ChevronUp, BarChart3, Target, Lightbulb, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import Stats from '../Stats';
import Trust from '../Trust';
import Newsletter from '../Newsletter';
import ContactForm from '../ContactForm';

// Dodanie importu czcionki Inter
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const BiznnesPlany = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const services = [
    { icon: <FileText className="w-8 h-8" />, title: "Biznes plany dla dotacji i pożyczek", description: "Przygotowujemy plany strategiczne wymagane w procesie aplikowania o środki unijne i krajowe." },
    { icon: <Target className="w-8 h-8" />, title: "Studia wykonalności", description: "Oceniamy realność i opłacalność planowanych inwestycji, analizując aspekty rynkowe, techniczne i finansowe." },
    { icon: <TrendingUp className="w-8 h-8" />, title: "Prognozy finansowe", description: "Tworzymy realistyczne prognozy przepływów pieniężnych, rachunku zysków i strat oraz bilansu." },
    { icon: <Calculator className="w-8 h-8" />, title: "Analizy rentowności", description: "Badamy opłacalność poszczególnych projektów lub całej działalności." },
    { icon: <BarChart3 className="w-8 h-8" />, title: "Strategie finansowe", description: "Doradzamy w budowaniu długoterminowych strategii finansowych dla Twojej firmy." },
    { icon: <Lightbulb className="w-8 h-8" />, title: "Wyceny przedsiębiorstw", description: "Profesjonalne wyceny firm do transakcji M&A, inwestycji lub procesów restrukturyzacyjnych." }
  ];

  const benefits = [
    { icon: <Shield className="w-8 h-8" />, title: "Zwiększenie wiarygodności", description: "projektu" },
    { icon: <Lightbulb className="w-8 h-8" />, title: "Lepsze decyzje", description: "inwestycyjne" },
    { icon: <Users className="w-8 h-8" />, title: "Ułatwienie pozyskania", description: "finansowania" },
    { icon: <Target className="w-8 h-8" />, title: "Minimalizacja", description: "ryzyka" }
  ];

  const faqs = [
    { question: "Do czego potrzebny jest biznes plan?", answer: "Biznes plan jest niezbędny do pozyskania finansowania (dotacje, kredyty, inwestorzy), planowania rozwoju firmy, oceny opłacalności nowych projektów, a także jako narzędzie wewnętrznego zarządzania i monitorowania postępów." },
    { question: "Jakie elementy powinien zawierać profesjonalny biznes plan?", answer: "Standardowy biznes plan powinien zawierać m.in.: streszczenie, opis firmy i zespołu, analizę rynku i konkurencji, plan marketingowy, plan operacyjny, harmonogram realizacji oraz kluczowe analizy finansowe (prognozy, przepływy pieniężne, analizy rentowności)." },
    { question: "Czy pomagacie w przygotowaniu biznes planu dla startupów?", answer: "Tak, specjalizujemy się w tworzeniu biznes planów dla startupów, uwzględniając ich specyfikę, model biznesowy i potrzeby finansowania. Pomożemy Ci przekonać potencjalnych inwestorów do Twojego innowacyjnego pomysłu." }
  ];

  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);
  
  const handleContactScroll = () => {
    const contactSection = document.querySelector('#kontakt');
    if (contactSection) contactSection.scrollIntoView({ behavior: 'smooth' });
  };

  const SectionTitle = ({ children, className = '' }) => (
    <h2 
      className={`text-2xl sm:text-3xl md:text-5xl text-white text-center uppercase leading-tight ${className}`}
      style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}
    >
      {children}
    </h2>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative pt-28 sm:pt-32 pb-16 lg:pt-48 lg:pb-32 overflow-hidden bg-black">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-[15%] animate-float"><FileText className="w-24 h-24 text-violet-600/40" /></div>
          <div className="absolute bottom-1/4 right-[15%] animate-float animation-delay-2000"><Calculator className="w-32 h-32 text-violet-600/40" /></div>
          <div className="absolute top-20 right-[40%] animate-float animation-delay-1000"><BarChart3 className="w-20 h-20 text-violet-600/40" /></div>
        </div>
        <div className="relative z-10 mx-auto text-center px-6">
            <h1 className="text-3xl sm:text-4xl md:text-6xl text-white mb-6 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
                Biznes Plany i Analizy Finansowe
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-6 sm:mb-8 font-semibold max-w-3xl mx-auto">
                Tworzymy solidne fundamenty dla Twojego sukcesu
            </p>
            <p className="text-base sm:text-lg text-gray-300 mb-8 sm:mb-10 max-w-3xl mx-auto">
                Solidny biznes plan to klucz do pozyskania finansowania i skutecznego rozwoju. Przygotowujemy profesjonalne analizy i plany, które precyzyjnie odzwierciedlają potencjał Twojej firmy i przekonują instytucje finansujące.
            </p>
            <div className="flex justify-center">
                <button onClick={handleContactScroll} className="group flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white font-semibold rounded-full transition-all duration-300 hover:from-violet-700 hover:to-violet-800 hover:scale-105 text-center">
                    <span>Bezpłatna konsultacja</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
            </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 md:py-24 bg-black">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <SectionTitle className="mb-6">Profesjonalne planowanie i analizy finansowe</SectionTitle>
            <p className="text-lg text-gray-300 max-w-4xl mx-auto">
              Niezależnie od tego, czy starasz się o dofinansowanie, pożyczkę, czy poszukujesz inwestora, rzetelny biznes plan i kompleksowa analiza finansowa to Twoja wizytówka. Tworzymy dokumenty, które nie tylko spełniają wymogi formalne, ale przede wszystkim strategicznie prezentują Twój pomysł.
            </p>
          </div>

          <div className="mb-24">
            <h3 className="text-2xl text-white font-bold mb-10 text-center uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Nasze usługi w tym zakresie
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <div key={index} className="backdrop-blur-md bg-white/5 rounded-xl p-6 border border-white/10 hover:border-violet-600/50 transition-all duration-300">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-xl mb-4 text-violet-500">
                    {service.icon}
                  </div>
                  <h4 className="text-lg font-bold text-white mb-3">{service.title}</h4>
                  <p className="text-gray-400 text-sm">{service.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="backdrop-blur-md bg-white/5 rounded-2xl md:rounded-3xl p-5 sm:p-8 lg:p-10 border border-white/10">
            <h3 className="text-3xl font-bold mb-12 text-center text-white uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Dlaczego precyzyjne planowanie jest tak ważne?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-full mb-4 text-violet-500">
                    {benefit.icon}
                  </div>
                  <h4 className="font-bold text-white text-lg mb-1">{benefit.title}</h4>
                  <p className="text-gray-400 text-sm">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Stats />
      <Trust />

      <section className="py-16 md:py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6">
          <SectionTitle className="mb-12">FAQ: Biznes plany i analizy finansowe</SectionTitle>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="backdrop-blur-md bg-white/5 rounded-xl border border-white/10 overflow-hidden transition-all duration-300 hover:border-violet-600/30">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-4 text-left flex justify-between items-center group">
                  <span className="font-semibold text-lg text-white pr-4">{faq.question}</span>
                  {openFaq === index ? 
                    <ChevronUp className="w-5 h-5 text-violet-600 flex-shrink-0" /> :
                    <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-violet-600 transition-colors flex-shrink-0" />
                  }
                </button>
                <div className={`transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="overflow-hidden px-6 pb-4">
                    <p className="text-gray-300 pt-2">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Newsletter />
      <div id="kontakt"><ContactForm /></div>

      <style>{`
          @keyframes float {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-20px) rotate(5deg); }
          }
          .animate-float {
              animation: float 8s ease-in-out infinite;
          }
          .animation-delay-1000 { animation-delay: 1s; }
          .animation-delay-2000 { animation-delay: 2s; }
          .max-h-96 { max-height: 24rem; }
      `}</style>
    </div>
  );
};

export default BiznnesPlany;
